// ════════════════════════════════════════════════════════════════════════════
// AdminConsole.jsx — Chrome del panel de administración (Diseño A "Consola")
// Sidebar de navegación + topbar + dashboard "Resumen".
//
// Se integra en AdminPage.jsx SIN tocar la lógica de las pestañas existentes.
// El dashboard usa datos REALES: usuarios (online/baneados/cristales), noticias
// y /api/admin/survey/stats. Navega a las pestañas reales mediante onNavigate().
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  LayoutDashboard, Newspaper, Users, ShoppingCart, Package, Megaphone,
  BarChart2, ClipboardList, Terminal, Settings, ChevronRight, RefreshCw,
  Snowflake, Gift, Ban, Eye, TrendingUp, Activity, Bell, Crown, Receipt,
  Map as MapIcon, Euro, AlertTriangle, Hourglass, Scale,
} from 'lucide-react';
import { dayKey, fmtDate, fmtDateTime, fmtEur, fmtNum, parseDate } from '../lib/fmt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

// Logo del servidor, servido desde nuestro propio /uploads (antes, un CDN externo)
const LOGO = `${BACKEND_URL}/uploads/frostbound_logo_final.png`;

// ── Definición de navegación: la clave coincide con el `tab` de AdminPage ──────
export const ADMIN_NAV = [
  { key: 'dashboard',    label: 'Resumen',      Icon: LayoutDashboard },
  { key: 'noticias',     label: 'Noticias',     Icon: Newspaper },
  { key: 'usuarios',     label: 'Usuarios',     Icon: Users, showCount: true },
  { key: 'referidos',    label: 'Referidos',    Icon: Gift },
  { key: 'tienda',       label: 'Tienda',       Icon: ShoppingCart },
  { key: 'compras',      label: 'Compras',      Icon: Receipt },
  { key: 'addons',       label: 'Addons',       Icon: Package },
  { key: 'servidor',     label: 'Servidor',     Icon: Megaphone },
  { key: 'mapa',         label: 'Mapa',         Icon: MapIcon },
  { key: 'estadisticas', label: 'Cuentas',      Icon: BarChart2 },
  { key: 'encuesta',     label: 'Encuesta',     Icon: ClipboardList },
  { key: 'dev',          label: 'Dev',          Icon: Terminal },
];
const TITLES = ADMIN_NAV.reduce((m, n) => (m[n.key] = n.label, m), {});

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────────────────────────────────────
export function AdminSidebar({ tab, setTab, usersCount = 0, username }) {
  return (
    <aside className="adc-side">
      <div className="adc-brand">
        <img src={LOGO} alt="Frostbound" />
        <span>FROSTBOUND</span>
      </div>
      <nav className="adc-nav">
        {ADMIN_NAV.map(({ key, label, Icon, showCount }) => (
          <button
            key={key}
            className={`adc-nav-item ${tab === key ? 'adc-nav-item--active' : ''}`}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            title={label}
          >
            <Icon size={18} />
            <span className="adc-nav-label">{label}</span>
            {showCount && usersCount > 0 && <span className="adc-nav-badge">{usersCount}</span>}
          </button>
        ))}
      </nav>
      <div className="adc-side-foot">
        <div className="adc-side-user">
          <span className="adc-avatar">{(username || 'A').slice(0, 1).toUpperCase()}</span>
          <span className="adc-side-user-txt">
            <b>{username || 'Administrador'}</b>
            <small>Administrador</small>
          </span>
        </div>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Topbar
// ─────────────────────────────────────────────────────────────────────────────
export function AdminTopbar({ tab, onlineCount = 0, username, onRefresh }) {
  const item = ADMIN_NAV.find((n) => n.key === tab) || ADMIN_NAV[0];
  const Icon = item.Icon;
  return (
    <header className="adc-topbar">
      <div className="adc-crumb">
        <Icon size={15} style={{ color: '#00e5ff' }} />
        <span className="adc-crumb-root">Admin</span>
        <ChevronRight size={12} style={{ color: 'rgba(255,255,255,0.3)' }} />
        <b>{TITLES[tab] || item.label}</b>
      </div>
      <div className="adc-topbar-r">
        <span className="adc-online-pill"><span className="adc-dot" />{onlineCount} online</span>
        {onRefresh && (
          <button className="adc-icon-btn" onClick={onRefresh} title="Actualizar datos"><RefreshCw size={15} /></button>
        )}
        <span className="adc-avatar adc-avatar--sm" title={username}>{(username || 'A').slice(0, 1).toUpperCase()}</span>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini-gráficas (SVG, sin dependencias)
// ─────────────────────────────────────────────────────────────────────────────
const accountsText = (v) => `${v} ${v === 1 ? 'cuenta' : 'cuentas'}`;

function BarChart({ data, labels = [], h = 92, color = '#69CCF0', valueText = accountsText }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...data);
  return (
    <div className="adc-bars-chart" style={{ height: h, position: 'relative' }}>
      {data.map((v, i) => (
        <div key={i} className="adc-bar"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover((curr) => (curr === i ? null : curr))}
          style={{
            position: 'relative',
            height: `${(v / max) * 100}%`,
            background: `linear-gradient(180deg, ${color}, ${color}33)`,
            opacity: hover === i ? 1 : (i === data.length - 1 ? 1 : 0.72),
            cursor: 'default',
          }}
        >
          {hover === i && (
            <div style={{
              position: 'absolute',
              bottom: 'calc(100% + 6px)',
              left: '50%', transform: 'translateX(-50%)',
              background: 'rgba(8,12,28,0.97)',
              border: `1px solid ${color}66`,
              borderRadius: 6,
              padding: '4px 8px',
              fontSize: 11, lineHeight: 1.3,
              color: '#e0f7ff', whiteSpace: 'nowrap',
              pointerEvents: 'none', zIndex: 5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}>
              <b style={{ color: '#e0f7ff' }}>{valueText(v)}</b>
              {labels[i] && <div style={{ color: 'rgba(224,247,255,0.55)', fontSize: 10 }}>{labels[i]}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
function Donut({ pct, label, sub, color = '#22c55e', size = 96, stroke = 9 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <div className="adc-donut" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: 'stroke-dashoffset .6s ease' }} />
      </svg>
      <div className="adc-donut-c">
        <span className="adc-donut-val">{label}</span>
        {sub && <span className="adc-donut-sub">{sub}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Panel contenedor
// ─────────────────────────────────────────────────────────────────────────────
function Panel({ title, Icon, right, children, className = '' }) {
  return (
    <section className={`adc-panel ${className}`}>
      <header className="adc-panel-h">
        <h3>{Icon && <Icon size={14} />}{title}</h3>
        {right}
      </header>
      <div className="adc-panel-body">{children}</div>
    </section>
  );
}
function Stat({ Icon, label, value, delta, color = '#00e5ff' }) {
  return (
    <div className="adc-stat">
      <div className="adc-stat-top">
        <span className="adc-stat-ico" style={{ color }}><Icon size={15} /></span>
        {delta != null && <span className="adc-stat-delta">{delta}</span>}
      </div>
      <div className="adc-stat-val">{value}</div>
      <div className="adc-stat-lbl">{label}</div>
    </div>
  );
}


// ── Economía: utilidades de formato ──────────────────────────────────────────
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const monthLabel = (m) => {
  if (!m) return '…';
  const [y, mm] = m.split('-').map(Number);
  return `${MONTHS[mm - 1]} ${y}`;
};
const lastMonths = (n) => {
  const [y, m] = dayKey(new Date()).split('-').map(Number);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
};
const shortDay = (k) => { const [, mm, dd] = k.split('-'); return `${dd}/${mm}`; };
const deltaText = (cur, prev) => {
  if (!prev) return cur ? 'mes anterior: 0 €' : 'sin ingresos';
  const pct = Math.round(((cur - prev) / prev) * 100);
  return `${pct >= 0 ? '▲' : '▼'} ${Math.abs(pct)} % vs mes anterior (${fmtEur(prev)})`;
};
const REASON_LABELS = {
  stripe_topup: 'Recargas (Stripe)', vote: 'Votos', survey: 'Encuesta', referral_referrer: 'Referidos (padrino)',
  referral_referred: 'Referidos (invitado)', admin_grant: 'Concedidos por admin',
};

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard "Resumen"
// ─────────────────────────────────────────────────────────────────────────────
export function AdminDashboard({ news = [], users = [], onNavigate, onOpenAccount, username }) {
  const [survey, setSurvey] = useState(null);
  const [month, setMonth] = useState('');           // '' = mes en curso
  const [eco, setEco] = useState(null);
  const [ecoError, setEcoError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setEcoError(null);
    axios.get(`${BACKEND_URL}/api/admin/economy/summary`, { params: month ? { month } : {}, withCredentials: true })
      .then(({ data }) => { if (!cancelled) setEco(data); })
      .catch((err) => { if (!cancelled) setEcoError(err?.response?.data?.detail || 'No se pudo cargar la economía.'); });
    return () => { cancelled = true; };
  }, [month]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/admin/survey/stats`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setSurvey(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const onlineCount = users.filter((u) => u.is_online).length;
  const bannedCount = users.filter((u) => u.is_banned).length;
  // Sin cuentas admin: sus saldos de prueba inflaban la cifra
  const crystalsTotal = users.filter((u) => u.role !== 'admin').reduce((s, u) => s + (u.frost_crystals || 0), 0);

  // Registros por día — últimos 14 días según el calendario de España.
  // created_at llega en UTC; dayKey() lo pasa a la fecha de Europe/Madrid, igual
  // que las claves de los días (antes se mezclaban UTC y hora local: barras
  // desplazadas un día y los registros de hoy no se contaban).
  const signups14 = useMemo(() => {
    const [y, m, dd] = dayKey(new Date()).split('-').map(Number);
    const days = Array.from({ length: 14 }, (_, i) => {
      // Mediodía UTC del día de calendario: inmune a los cambios de hora
      const d = new Date(Date.UTC(y, m - 1, dd - (13 - i), 12));
      return { key: d.toISOString().slice(0, 10), label: d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' }), count: 0 };
    });
    const idx = new Map(days.map((d, i) => [d.key, i]));
    let any = false;
    users.forEach((u) => {
      const key = dayKey(u.created_at);
      if (key && idx.has(key)) { days[idx.get(key)].count++; any = true; }
    });
    return any ? { counts: days.map((d) => d.count), labels: days.map((d) => d.label) } : null;
  }, [users]);

  // Últimos 6 logins (de MySQL via /admin/users)
  const recentLogins = useMemo(() => {
    return [...users]
      .filter((u) => u.last_login)
      .sort((a, b) => (parseDate(b.last_login) || 0) - (parseDate(a.last_login) || 0))
      .slice(0, 6);
  }, [users]);

  const recentUsers = useMemo(() => {
    return [...users]
      .sort((a, b) => (parseDate(b.created_at) || 0) - (parseDate(a.created_at) || 0))
      .slice(0, 6);
  }, [users]);

  const surveyPct = survey && survey.total ? Math.round((survey.reward_granted / survey.total) * 100) : 0;
  const lastNews = news[0];

  // Centro de tareas — solo señales reales y accionables
  const tasks = [];
  const att = eco?.attention;
  if (att?.grants_failed?.length) {
    att.grants_failed.forEach((g) => tasks.push({
      Icon: AlertTriangle, color: '#f87171',
      title: `Entrega fallida: ${g.character || 'personaje ?'} (encargo #${g.id})`,
      sub: `${g.note || 'sin detalle'} · compra cobrada: devolver a mano`,
      action: g.username ? 'Ver ficha' : 'Ver', onClick: g.username ? () => onOpenAccount?.(g.username) : null, go: 'estadisticas',
    }));
  }
  if (att?.grants_pending?.length) {
    const oldest = att.grants_pending[att.grants_pending.length - 1];
    tasks.push({
      Icon: Hourglass, color: '#fbbf24',
      title: `${att.grants_pending.length} ${att.grants_pending.length === 1 ? 'entrega pendiente' : 'entregas pendientes'} del worldserver`,
      sub: `Se aplican cuando el personaje entra · la más antigua: ${oldest.character || '?'}, ${fmtDateTime(oldest.created_at)}`,
      action: 'Ver ficha', onClick: oldest.username ? () => onOpenAccount?.(oldest.username) : null, go: 'estadisticas',
    });
  }
  if (att?.ledger_mismatches > 0)
    tasks.push({ Icon: Scale, color: '#f87171', title: `Ledger de cristales: ${att.ledger_mismatches} ${att.ledger_mismatches === 1 ? 'cuenta descuadrada' : 'cuentas descuadradas'}`, sub: 'El saldo no coincide con la suma de movimientos (¿cambio a mano en Mongo?)', action: 'Ver', go: 'dev' });
  if (att?.referrals_to_review > 0)
    tasks.push({ Icon: Gift, color: '#fb923c', title: `${att.referrals_to_review} ${att.referrals_to_review === 1 ? 'referido' : 'referidos'} por revisar (misma IP)`, sub: 'Requieren aprobación o rechazo manual', action: 'Revisar', go: 'referidos' });
  if (survey && survey.reward_pending > 0)
    tasks.push({ Icon: Gift, color: '#22c55e', title: `${survey.reward_pending} ${survey.reward_pending === 1 ? 'recompensa de encuesta pendiente' : 'recompensas de encuesta pendientes'}`, sub: 'Jugadores que respondieron y esperan login al juego', action: 'Ver', go: 'encuesta' });
  if (bannedCount > 0)
    tasks.push({ Icon: Ban, color: '#f87171', title: `${bannedCount} ${bannedCount === 1 ? 'cuenta baneada' : 'cuentas baneadas'}`, sub: 'Revisa o restaura accesos bloqueados', action: 'Revisar', go: 'usuarios' });
  if (onlineCount > 0)
    tasks.push({ Icon: Users, color: '#4ade80', title: `${onlineCount} ${onlineCount === 1 ? 'jugador conectado' : 'jugadores conectados'} ahora`, sub: 'Buen momento para un anuncio o evento', action: 'Anunciar', go: 'servidor' });
  tasks.push({ Icon: Newspaper, color: '#69CCF0', title: lastNews ? `Última noticia: ${lastNews.title}` : 'Aún no has publicado noticias', sub: lastNews ? `Publicada el ${fmtDate(lastNews.published_at)}` : 'Crea la primera novedad para la comunidad', action: lastNews ? 'Gestionar' : 'Crear', go: 'noticias' });

  const QUICK = [
    { Icon: Newspaper, label: 'Nueva noticia', color: '#69CCF0', go: 'noticias' },
    { Icon: Megaphone, label: 'Anuncio global', color: '#FFAA44', go: 'servidor' },
    { Icon: Gift, label: 'Cristales', color: '#22c55e', go: 'dev' },
    { Icon: Users, label: 'Buscar cuenta', color: '#a78bfa', go: 'usuarios' },
  ];

  return (
    <div className="adc-dash">
      {/* KPIs */}
      <div className="adc-stats-row">
        <Stat Icon={Users}       label="Jugadores online"      value={onlineCount.toLocaleString()} color="#4ade80" />
        <Stat Icon={Users}       label="Cuentas totales"       value={users.length.toLocaleString()} color="#00e5ff" />
        <Stat Icon={Snowflake}   label="Cristales en circulación (sin admins)" value={fmtNum(crystalsTotal)} color="#69CCF0" />
        <Stat Icon={Newspaper}   label="Noticias publicadas"   value={news.length.toLocaleString()} color="#a78bfa" />
        <Stat Icon={ClipboardList} label="Respuestas encuesta"  value={survey ? survey.total.toLocaleString() : '—'} color="#22d3ee" />
        <Stat Icon={Ban}         label="Cuentas baneadas"      value={bannedCount.toLocaleString()} color="#f87171" />
      </div>

      {/* Accesos rápidos */}
      <div className="adc-quickbar">
        {QUICK.map((q) => (
          <button key={q.label} className="adc-quick" style={{ '--qc': q.color }} onClick={() => onNavigate(q.go)}>
            <q.Icon size={15} /><span>{q.label}</span>
          </button>
        ))}
      </div>

      {/* Economía (sin cuentas admin) */}
      <Panel title={`Economía · ${monthLabel(eco?.month)}`} Icon={Euro} className="adc-eco"
        right={(
          <select className="adm-select" value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Mes" data-testid="eco-month">
            <option value="">Mes en curso</option>
            {lastMonths(12).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        )}>
        {ecoError && <div className="adc-empty" style={{ color: '#f87171' }}>{ecoError}</div>}
        {!eco && !ecoError && <div className="adc-empty">Cargando…</div>}
        {eco && (
          <div data-testid="eco-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="adm-kpis">
              <div className="adm-kpi">
                <span className="adm-kpi-lbl">Ingresos</span>
                <span className="adm-kpi-val" style={{ color: '#fcd34d' }}>{fmtEur(eco.revenue_eur)}</span>
                <span className="adm-kpi-sub">{deltaText(eco.revenue_eur, eco.revenue_prev_eur)}</span>
              </div>
              <div className="adm-kpi"><span className="adm-kpi-lbl">Recargas</span><span className="adm-kpi-val">{fmtNum(eco.topups)}</span></div>
              <div className="adm-kpi">
                <span className="adm-kpi-lbl">Compradores</span><span className="adm-kpi-val">{fmtNum(eco.buyers)}</span>
                <span className="adm-kpi-sub">{eco.new_buyers} {eco.new_buyers === 1 ? 'nuevo' : 'nuevos'} · {eco.buyers_all_time} desde siempre</span>
              </div>
              <div className="adm-kpi"><span className="adm-kpi-lbl">Media por comprador</span><span className="adm-kpi-val">{eco.buyers ? fmtEur(eco.avg_per_buyer_eur) : '—'}</span></div>
              <div className="adm-kpi">
                <span className="adm-kpi-lbl">Ventas en tienda</span><span className="adm-kpi-val">{fmtNum(eco.units_sold)}</span>
                <span className="adm-kpi-sub">{fmtNum(eco.crystals_spent)} ❄ cobrados</span>
              </div>
              <div className="adm-kpi">
                <span className="adm-kpi-lbl">Bonos sin canjear</span>
                <span className="adm-kpi-val">{fmtNum(Object.values(eco.vouchers_outstanding || {}).reduce((a, b) => a + b, 0))}</span>
              </div>
            </div>

            <div className="adc-grid-2">
              <div>
                <p className="adc-eco-h">Ingresos por día · últimos 30 días</p>
                {eco.revenue_by_day.some((d) => d.eur > 0) ? (<>
                  <BarChart data={eco.revenue_by_day.map((d) => d.eur)} h={130} color="#fcd34d"
                    labels={eco.revenue_by_day.map((d) => `${shortDay(d.day)} · ${d.topups} ${d.topups === 1 ? 'recarga' : 'recargas'}`)}
                    valueText={(v) => fmtEur(v)} />
                  <div className="adc-axis"><span>hace 30 días</span><span>hoy</span></div>
                </>) : <div className="adc-empty">Sin recargas en los últimos 30 días.</div>}
              </div>
              <div>
                <p className="adc-eco-h">Ventas por producto · {monthLabel(eco.month)}</p>
                {eco.sales_by_product.length ? (
                  <table className="adc-table adc-sales">
                    <thead><tr><th>Producto</th><th style={{ textAlign: 'right' }}>Uds.</th><th style={{ textAlign: 'right' }}>❄ cobrados</th><th style={{ textAlign: 'right' }}>Con bono</th></tr></thead>
                    <tbody>
                      {eco.sales_by_product.map((r) => {
                        const max = Math.max(...eco.sales_by_product.map((x) => x.units));
                        return (
                          <tr key={r.product_id}>
                            <td>
                              <div className="adc-sales-name">{r.name}</div>
                              <div className="adc-sales-bar" style={{ width: `${(r.units / max) * 100}%` }} />
                            </td>
                            <td style={{ textAlign: 'right' }}><b>{fmtNum(r.units)}</b></td>
                            <td style={{ textAlign: 'right' }}>{fmtNum(r.crystals)}</td>
                            <td style={{ textAlign: 'right' }} className="adc-mut">{r.with_voucher || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : <div className="adc-empty">Sin ventas este mes.</div>}
              </div>
            </div>

            <div className="adc-grid-2">
              <div>
                <p className="adc-eco-h">Cristales emitidos · {monthLabel(eco.month)} · {fmtNum(eco.crystals_issued_total)} ❄</p>
                {Object.keys(eco.crystals_issued).length ? (
                  <div className="adc-reasons">
                    {Object.entries(eco.crystals_issued).map(([reason, n]) => (
                      <div key={reason} className="adc-reason">
                        <span>{REASON_LABELS[reason] || reason}</span>
                        <span className="adc-reason-bar"><span style={{ width: `${(n / eco.crystals_issued_total) * 100}%` }} /></span>
                        <b>{fmtNum(n)}</b>
                      </div>
                    ))}
                  </div>
                ) : <div className="adc-empty">El ledger no tiene movimientos este mes.</div>}
              </div>
              <div>
                <p className="adc-eco-h">Quién ha pagado · {monthLabel(eco.month)}</p>
                {eco.top_buyers.length ? (
                  <div className="adc-reasons">
                    {eco.top_buyers.map((b) => (
                      <div key={b.username} className="adc-reason">
                        <button type="button" className="adm-link" onClick={() => b.username && onOpenAccount?.(b.username)}>{b.username || '—'}</button>
                        <span />
                        <b style={{ color: '#fcd34d' }}>{fmtEur(b.eur)}</b>
                      </div>
                    ))}
                  </div>
                ) : <div className="adc-empty">Nadie este mes.</div>}
              </div>
            </div>
            <p className="adc-mut" style={{ fontSize: 11, margin: 0 }}>
              Cifras sin las {eco.excluded_admins} cuentas admin. Meses y días según la hora de España. El ledger de cristales existe desde el 09/10/2026.
            </p>
          </div>
        )}
      </Panel>

      <div className="adc-grid-2">
        {/* Registros */}
        <Panel title="Registros · últimos 14 días" Icon={TrendingUp}
          right={<span className="adc-pill-mini">{users.length} cuentas</span>}>
          {signups14
            ? (<><BarChart data={signups14.counts} labels={signups14.labels} h={150} color="#69CCF0" />
                <div className="adc-axis"><span>hace 14 días</span><span>hoy</span></div></>)
            : <div className="adc-empty">No hay fechas de registro disponibles para graficar.</div>}
        </Panel>

        {/* Centro de tareas */}
        <Panel title="Requiere tu atención" Icon={Bell}
          right={<span className="adc-pill-mini" style={{ color: '#fbbf24' }}>{tasks.length}</span>}>
          <div className="adc-tasks">
            {tasks.map((t, i) => (
              <div key={i} className="adc-task">
                <span className="adc-task-ico" style={{ color: t.color, background: `${t.color}1a`, borderColor: `${t.color}44` }}><t.Icon size={16} /></span>
                <div className="adc-task-txt"><b>{t.title}</b><span>{t.sub}</span></div>
                <button className="adc-task-btn" onClick={() => (t.onClick ? t.onClick() : onNavigate(t.go))}>{t.action}<ChevronRight size={13} /></button>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Últimos logins al juego */}
      <Panel title="Últimos logins al juego" Icon={Activity}
        right={<button className="adc-link-btn" onClick={() => onNavigate('usuarios')}>Ver todos <ChevronRight size={12} /></button>}>
        {recentLogins.length === 0 ? (
          <div className="adc-empty">Aún no hay logins registrados en el servidor de juego.</div>
        ) : (
          <table className="adc-table">
            <thead><tr><th>Estado</th><th>Usuario</th><th>Cuenta de juego</th><th>Último login</th><th>IP</th></tr></thead>
            <tbody>
              {recentLogins.map((u) => (
                <tr key={u._id}>
                  <td>
                    <span className="adc-udot" style={{
                      background: u.is_online ? '#4ade80' : '#3a4a5a',
                      boxShadow:  u.is_online ? '0 0 8px #4ade80' : 'none',
                    }} />
                    <span style={{ color: u.is_online ? '#4ade80' : 'rgba(224,247,255,0.45)', fontSize: 11, fontWeight: 600 }}>
                      {u.is_online ? 'EN LÍNEA' : 'offline'}
                    </span>
                  </td>
                  <td><b style={{ color: u.role === 'admin' ? '#FFD700' : '#00e5ff' }}>{u.username}</b></td>
                  <td className="adc-mut adc-mono">{u.game_account || u.username.toUpperCase()}</td>
                  <td className="adc-mut adc-mono">{fmtDateTime(u.last_login)}</td>
                  <td className="adc-mut adc-mono">{u.last_ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <div className="adc-grid-feed">
        {/* Últimos registros */}
        <Panel title="Últimos registros" Icon={Users}
          right={<button className="adc-link-btn" onClick={() => onNavigate('usuarios')}>Ver todos <ChevronRight size={12} /></button>}>
          <table className="adc-table">
            <thead><tr><th>Usuario</th><th>Correo</th><th>Registro</th><th>Cristales</th></tr></thead>
            <tbody>
              {recentUsers.map((u) => (
                <tr key={u._id}>
                  <td>
                    <span className="adc-udot" style={{ background: u.is_online ? '#4ade80' : '#3a4a5a' }} />
                    <b style={{ color: u.role === 'admin' ? '#FFD700' : '#00e5ff' }}>{u.username}</b>
                  </td>
                  <td className="adc-mut">{u.email}</td>
                  <td className="adc-mut adc-mono">{fmtDateTime(u.created_at)}</td>
                  <td><b style={{ color: '#69CCF0' }}>{fmtNum(u.frost_crystals || 0)}</b> ❄</td>
                </tr>
              ))}
              {recentUsers.length === 0 && <tr><td colSpan={4} className="adc-empty">Sin usuarios todavía.</td></tr>}
            </tbody>
          </table>
        </Panel>

        {/* Encuesta */}
        <Panel title="Encuesta de jugadores" Icon={ClipboardList}
          right={<button className="adc-link-btn" onClick={() => onNavigate('encuesta')}>Detalle <ChevronRight size={12} /></button>}>
          {survey && survey.total > 0 ? (
            <div className="adc-survey-mini">
              <Donut pct={surveyPct} label={`${surveyPct}%`} sub="ENTREGADA" color="#22c55e" />
              <div className="adc-survey-nums">
                <div><b>{survey.total.toLocaleString()}</b><span>respuestas</span></div>
                <div><b style={{ color: '#22c55e' }}>{survey.reward_granted.toLocaleString()}</b><span>recompensas entregadas</span></div>
                <div><b style={{ color: '#fbbf24' }}>{survey.reward_pending.toLocaleString()}</b><span>pendientes de login</span></div>
              </div>
            </div>
          ) : (
            <div className="adc-empty">Aún no hay respuestas de encuesta.</div>
          )}
        </Panel>
      </div>
    </div>
  );
}
